import {
  forwardRef,
  type CSSProperties,
  type ElementType,
  type ForwardedRef,
  type ReactElement,
} from 'react';

import {
  useBoxTheme,
  type BoxThemeLike,
} from './themeContext';

 

/**
 * <Box> — the legacy style-prop primitive.
 *
 * This file used to be a ~230-line styled-components template. It's now
 * pure JSX + inline styles, with theme token values resolved via
 * BoxThemeContext.
 *
 * WHY INLINE STYLES:
 *   - The prop API (<Box m={3} bg="gray.0" width={200}>) maps directly
 *     to concrete CSS values. There's no clean 1:1 Tailwind class for
 *     every possible (string|number) input.
 *   - Vitest runs with css:false; the many existing tests use
 *     toHaveStyle(...) to assert behaviour. Inline styles preserve that
 *     contract so we can migrate Box away from styled-components
 *     WITHOUT rewriting ~150 downstream test assertions.
 *   - Scheduled for REMOVAL in Phase 4/5 once feature-file callers
 *     are rewritten to raw JSX + Tailwind className.
 *
 * WHY via context:
 *   - Tests install testTheme via src/test/test-utils.jsx using the
 *     BoxThemeContext.Provider. Production wrapping is unnecessary —
 *     the context's default is the real light theme.
 */

type ScaleRef = number | string | undefined | null;
type DotNotationColorRef = string | undefined | null;

interface BoxLegacyProps {
  display?: CSSProperties['display'];

  m?: ScaleRef;
  mt?: ScaleRef;
  mr?: ScaleRef;
  mb?: ScaleRef;
  ml?: ScaleRef;
  p?: ScaleRef;
  pt?: ScaleRef;
  pr?: ScaleRef;
  pb?: ScaleRef;
  pl?: ScaleRef;

  bg?: DotNotationColorRef;
  color?: DotNotationColorRef;

  width?: ScaleRef;
  height?: ScaleRef;
  minWidth?: ScaleRef;
  minHeight?: ScaleRef;

  position?: CSSProperties['position'];
  top?: ScaleRef;
  right?: ScaleRef;
  bottom?: ScaleRef;
  left?: ScaleRef;

  lineHeight?: CSSProperties['lineHeight'];
  textAlign?: CSSProperties['textAlign'];

  border?: number | string;
  borderColor?: DotNotationColorRef;
  borderRadius?: number | string;
  borderWidth?: number | string;
  borderStyle?: CSSProperties['borderStyle'];
  borderRadii?: [number, number, number, number];

  boxShadow?: number | string;

  cursor?: CSSProperties['cursor'];
  ellipsis?: boolean;
}

export type BoxProps<T extends ElementType = 'div'> = BoxLegacyProps & {
  as?: T;
  className?: string;
  style?: CSSProperties;
  [key: string]: unknown;
};

function resolveScale(
  scale: BoxThemeLike['space'],
  value: ScaleRef,
): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') {
    if (Array.isArray(scale)) {
      const token = scale[value];
      if (token !== undefined) return String(token);
    } else if (scale && typeof scale === 'object') {
      const token = (scale as Record<string | number, string | number>)[value];
      if (token !== undefined) return String(token);
    }
    // Fallback: treat as rem
    return `${value}rem`;
  }
  return String(value);
}

function resolveColor(
  colors: BoxThemeLike['colors'],
  value: unknown,
): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string') return String(value);
  if (value.includes('.')) {
    const [family, shadeRaw] = value.split('.');
    const shade = Number.isNaN(Number(shadeRaw))
      ? (shadeRaw as string)
      : Number(shadeRaw);
    const entry = family ? colors[family] : undefined;
    if (Array.isArray(entry)) {
      const v = (entry as string[])[shade as number];
      return v ?? value;
    }
    if (entry && typeof entry === 'object') {
      const v = (entry as Record<string | number, string>)[shade as string | number];
      return v ?? value;
    }
    return value;
  }
  const direct = colors[value];
  if (typeof direct === 'string') return direct;
  return value;
}

function resolveDim(value: ScaleRef): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return `${value}px`;
  return String(value);
}

function resolveScaleToken(
  scale: BoxThemeLike['radii'] | BoxThemeLike['borders'] | BoxThemeLike['shadows'],
  value: number | string | undefined,
): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') {
    const token = Array.isArray(scale)
      ? (scale as (string | number)[])[value]
      : (scale as Record<string | number, string | number>)[value];
    return token !== undefined ? String(token) : undefined;
  }
  return value;
}

/**
 * Compute a CSSProperties object from the Box-style prop API. Exported
 * for Flex/Text to reuse.
 */
export function computeBoxStyle(
  props: BoxLegacyProps,
  theme: BoxThemeLike,
): CSSProperties {
  const style: CSSProperties = {};

  if (props.display !== undefined) style.display = props.display;

  const m = resolveScale(theme.space, props.m);
  const mt = resolveScale(theme.space, props.mt);
  const mr = resolveScale(theme.space, props.mr);
  const mb = resolveScale(theme.space, props.mb);
  const ml = resolveScale(theme.space, props.ml);
  if (m !== undefined) style.margin = m;
  if (mt !== undefined) style.marginTop = mt;
  if (mr !== undefined) style.marginRight = mr;
  if (mb !== undefined) style.marginBottom = mb;
  if (ml !== undefined) style.marginLeft = ml;

  const p = resolveScale(theme.space, props.p);
  const pt = resolveScale(theme.space, props.pt);
  const pr = resolveScale(theme.space, props.pr);
  const pb = resolveScale(theme.space, props.pb);
  const pl = resolveScale(theme.space, props.pl);
  if (p !== undefined) style.padding = p;
  if (pt !== undefined) style.paddingTop = pt;
  if (pr !== undefined) style.paddingRight = pr;
  if (pb !== undefined) style.paddingBottom = pb;
  if (pl !== undefined) style.paddingLeft = pl;

  // Only set bg / color when the caller provided them. Setting inline
  // `background: transparent` / `color: inherit` would override rules
  // from a styled(Box) wrapper (which still exists during the
  // migration) because inline styles beat stylesheet rules. When the
  // prop is undefined we leave the property off the style object.
  const bg = resolveColor(theme.colors, props.bg);
  if (bg !== undefined) style.backgroundColor = bg;
  const fg = resolveColor(theme.colors, props.color);
  if (fg !== undefined) style.color = fg;

  const width = resolveDim(props.width);
  const height = resolveDim(props.height);
  const minWidth = resolveDim(props.minWidth);
  const minHeight = resolveDim(props.minHeight);
  if (width !== undefined) style.width = width;
  if (height !== undefined) style.height = height;
  if (minWidth !== undefined) style.minWidth = minWidth;
  if (minHeight !== undefined) style.minHeight = minHeight;

  if (props.position !== undefined) style.position = props.position;
  const top = resolveDim(props.top);
  const right = resolveDim(props.right);
  const bottom = resolveDim(props.bottom);
  const left = resolveDim(props.left);
  if (top !== undefined) style.top = top;
  if (right !== undefined) style.right = right;
  if (bottom !== undefined) style.bottom = bottom;
  if (left !== undefined) style.left = left;

  if (props.lineHeight !== undefined) style.lineHeight = props.lineHeight;
  if (props.textAlign !== undefined) style.textAlign = props.textAlign;

  const border = resolveScaleToken(theme.borders, props.border);
  if (border !== undefined) style.border = border;

  // Legacy: default border-color to gray.3 when border prop is set
  // (either via the `border` shorthand or `borderWidth`/`borderStyle`).
  // When no border-related props are set, leave border-color alone so
  // styled(Box) wrappers can apply their own.
  const bc = resolveColor(theme.colors, props.borderColor);
  if (bc !== undefined) {
    style.borderColor = bc;
  } else if (
    props.border !== undefined
    || props.borderWidth !== undefined
    || props.borderStyle !== undefined
  ) {
    style.borderColor = resolveColor(theme.colors, 'gray.3') ?? 'transparent';
  }

  if (props.borderWidth !== undefined) {
    style.borderWidth =
      typeof props.borderWidth === 'number' ? `${props.borderWidth}px` : props.borderWidth;
  }
  if (props.borderStyle !== undefined) style.borderStyle = props.borderStyle;

  if (Array.isArray(props.borderRadii)) {
    const [tl, tr, br, bl] = props.borderRadii;
    style.borderTopLeftRadius = resolveScaleToken(theme.radii, tl);
    style.borderTopRightRadius = resolveScaleToken(theme.radii, tr);
    style.borderBottomRightRadius = resolveScaleToken(theme.radii, br);
    style.borderBottomLeftRadius = resolveScaleToken(theme.radii, bl);
  } else if (props.borderRadius !== undefined) {
    style.borderRadius = resolveScaleToken(theme.radii, props.borderRadius);
  } else if (props.borderRadii !== undefined) {
    // Legacy: non-array borderRadii means 'use radii[N] uniformly'.
    style.borderRadius = resolveScaleToken(
      theme.radii,
      props.borderRadii as unknown as number | string,
    );
  }
  // NOTE: intentionally no default border-radius. Previously Box
  // defaulted to radii[0]; doing so now would override styled(Box)
  // wrappers' border-radius rules via inline style (which wins over
  // stylesheet classes). Consumers that want zero radius can pass
  // borderRadius={0} explicitly.

  const shadow = resolveScaleToken(theme.shadows, props.boxShadow);
  if (shadow !== undefined) style.boxShadow = shadow;

  if (props.cursor !== undefined) style.cursor = props.cursor;

  if (props.ellipsis) {
    style.whiteSpace = 'nowrap';
    style.overflow = 'hidden';
    style.textOverflow = 'ellipsis';
    style.overflowWrap = 'break-word';
    style.wordBreak = 'break-all';
  }

  return style;
}

// Keys we consume from props to build `style` — never forwarded to DOM.
const RESERVED_KEYS: ReadonlySet<string> = new Set([
  'display',
  'm', 'mt', 'mr', 'mb', 'ml',
  'p', 'pt', 'pr', 'pb', 'pl',
  'bg', 'color',
  'width', 'height', 'minWidth', 'minHeight',
  'position', 'top', 'right', 'bottom', 'left',
  'lineHeight', 'textAlign',
  'border', 'borderColor', 'borderRadius', 'borderWidth', 'borderStyle', 'borderRadii',
  'boxShadow',
  'cursor', 'ellipsis',
]);

// Legacy $-prefixed transient props (styled-components idiom) that some
// callers still pass directly. We translate them to the canonical
// style-prop names rather than forwarding to the DOM.
const BOX_DOLLAR_ALIASES: Record<string, string> = {
  $display: 'display',
  $m: 'm', $mt: 'mt', $mr: 'mr', $mb: 'mb', $ml: 'ml',
  $p: 'p', $pt: 'pt', $pr: 'pr', $pb: 'pb', $pl: 'pl',
  $bg: 'bg', $color: 'color',
  $width: 'width', $height: 'height', $minWidth: 'minWidth', $minHeight: 'minHeight',
  $position: 'position', $top: 'top', $right: 'right', $bottom: 'bottom', $left: 'left',
  $lineHeight: 'lineHeight', $textAlign: 'textAlign',
  $border: 'border', $borderColor: 'borderColor',
  $borderRadius: 'borderRadius', $borderWidth: 'borderWidth', $borderStyle: 'borderStyle',
  $borderRadii: 'borderRadii',
  $boxShadow: 'boxShadow',
  $cursor: 'cursor', $ellipsis: 'ellipsis',
};

export function splitBoxProps(
  props: Record<string, unknown>,
): { styleProps: BoxLegacyProps; rest: Record<string, unknown> } {
  const styleProps: Record<string, unknown> = {};
  const rest: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (RESERVED_KEYS.has(key)) {
      styleProps[key] = props[key];
      continue;
    }
    const aliased = BOX_DOLLAR_ALIASES[key];
    if (aliased !== undefined) {
      if (!(aliased in styleProps)) {
        styleProps[aliased] = props[key];
      }
      continue;
    }
    // Drop any stray `$foo` props that weren't explicit aliases — the
    // legacy styled-components transient-prop convention means these
    // were never meant for the DOM. Forwarding them would produce
    // 'Invalid attribute name' / 'unknown prop' React warnings.
    if (key.startsWith('$')) continue;
    rest[key] = props[key];
  }
  return { styleProps: styleProps as BoxLegacyProps, rest };
}

interface BoxComponent {
  <T extends ElementType = 'div'>(
    props: BoxProps<T> & { ref?: ForwardedRef<HTMLElement> },
  ): ReactElement | null;
  displayName?: string;
}

export const Box = forwardRef<HTMLElement, BoxProps>(function Box(props, ref) {
  const theme = useBoxTheme();
  const {
    as: asComponent = 'div',
    style: callerStyle,
    ...rawRest
  } = props as BoxProps & { style?: CSSProperties };

  const { styleProps, rest } = splitBoxProps(rawRest as Record<string, unknown>);
  const computed = computeBoxStyle(styleProps, theme);
  const finalStyle = callerStyle ? { ...computed, ...callerStyle } : computed;
  const Component = asComponent as ElementType;

  return <Component ref={ref} style={finalStyle} {...(rest as Record<string, unknown>)} />;
}) as unknown as BoxComponent;

(Box as unknown as { displayName?: string }).displayName = 'Box';
